import { alertVariants } from "@visiblethread/react-components";
import localhost, {
  downloadFileFromResponse,
  downloadWithIFrame,
} from "apis/localhost";
import { MULTIPART_FORM_DATA_CONTENT_TYPE } from "constants/app";
import {
  DICTIONARY_NAME_ALREADY_EXIST,
  DICTIONARY_NAME_EMPTY,
  DICTIONARY_NAME_INVALID_CHARACTERS,
  dictionaryNotFound,
  FILE_SIZE_INVALID,
  hasBeenAlreadyDeletedError,
  menuOptionLockError,
} from "constants/dictionary";
import { DICTIONARIES_HOME, getDictionaryPath } from "constants/routes";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  clearJobQueue,
  disableNavBarAndSideBar,
  hideInlineAlert,
  removeFromJobQueue,
  showGlobalAlert,
  showInlineAlert,
} from "slices/app/appSlice";
import { selectUserWorkspaceId } from "slices/app/selectors";
import {
  getDictionaries,
  resetEditMode,
  setDictionary,
  setLocked,
  setModeToEdit,
  setModeToView,
} from "slices/dictionary/dictionarySlice";
import { getErrorMessage } from "utils";
import { formatRequest, formatResponse } from "utils/dictionary";
import getNextAvailableTitle from "utils/getNextAvailableTitle";

import { selectDictionary, selectDictionaryId } from "../selectors";

const MULTIPLE_DICTIONARIES = MULTIPART_FORM_DATA_CONTENT_TYPE;
const getDictionaryNotFound = () => ({
  variant: alertVariants.warning,
  text: dictionaryNotFound,
});

const handleMenuOptionLockError = (dispatch) => {
  dispatch(setLocked(true));
  dispatch(
    showInlineAlert({
      variant: alertVariants.warning,
      text: menuOptionLockError,
    })
  );
};

export const fetchDictionary = (dictionaryId) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));
  const state = getState();
  const workspaceId = selectUserWorkspaceId(state);

  try {
    const rsp = await localhost.get("/dictionary", {
      params: { dictionaryId },
    });
    const { data } = rsp;
    if (workspaceId !== data.workspaceId) {
      dispatch(showGlobalAlert(getDictionaryNotFound()));
      return false;
    }

    dispatch(setDictionary(formatResponse(rsp.data)));
    return rsp.data;
  } catch (err) {
    if (err.response.status === 404) {
      dispatch(showGlobalAlert(getDictionaryNotFound()));
    } else {
      dispatch(showErrorModal(err));
    }
    return false;
  } finally {
    dispatch(clearJobQueue());
  }
};

export const openEditMode = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const { id } = getState().dictionaries.edit.editDictionary;
  await localhost
    .get("/dictionary/lock", {
      params: {
        id,
      },
    })
    .then(() => {
      dispatch(setModeToEdit());
      dispatch(hideInlineAlert());
      dispatch(disableNavBarAndSideBar(true));
    })
    .catch((error) => {
      const { errorCode, message } = error.response.data;
      if (errorCode === "DICTIONARY_LOCKED") {
        dispatch(setLocked(true));
        dispatch(
          showInlineAlert({
            variant: alertVariants.warning,
            text: message,
          })
        );
      } else if (errorCode === "DICTIONARY_NOT_FOUND") {
        dispatch(
          showInlineAlert({
            variant: alertVariants.danger,
            text: hasBeenAlreadyDeletedError,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const closeEditMode = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));
  const state = getState();
  const id = selectDictionaryId(state);

  await localhost
    .get("/dictionary/unlock", {
      params: {
        id,
      },
    })
    .then(() => {
      dispatch(fetchDictionary(id));
      dispatch(resetEditMode());
      dispatch(disableNavBarAndSideBar(false));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const fetchDictionaries = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/dictionary/list", {})
    .then((response) => {
      const { data } = response;
      dispatch(getDictionaries(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const saveChanges = () => async (dispatch, getState) => {
  const state = getState();
  const editDictionary = selectDictionary(state);
  const isEditing = Boolean(editDictionary.id);
  const requestAction = isEditing ? "put" : "post";

  dispatch(addToJobQueue("saveDictionaryChanges"));

  try {
    const params = formatRequest(editDictionary);
    const rsp = await localhost[requestAction]("/dictionary", params);
    const { data } = rsp;

    dispatch(setDictionary(formatResponse(data)));
    dispatch(setModeToView());
    dispatch(disableNavBarAndSideBar(false));

    return data;
  } catch (error) {
    const { errorCode, message } = error.response.data;

    if (
      errorCode === DICTIONARY_NAME_INVALID_CHARACTERS ||
      errorCode === DICTIONARY_NAME_ALREADY_EXIST ||
      errorCode === DICTIONARY_NAME_EMPTY ||
      errorCode === FILE_SIZE_INVALID
    ) {
      dispatch(
        showGlobalAlert({
          variant: alertVariants.danger,
          text: message,
        })
      );
    } else {
      dispatch(showErrorModal(error));
    }

    return false;
  } finally {
    dispatch(removeFromJobQueue("saveDictionaryChanges"));
  }
};

export const deleteDictionary =
  (dictionaryId, dictionaryName, navigate) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .delete("/dictionary/edit/delete", {
        params: { dictionaryId },
      })
      .then((response) => {
        const { data } = response;
        dispatch(getDictionaries(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `"${dictionaryName}" successfully deleted`,
          })
        );
        navigate(DICTIONARIES_HOME);
      })
      .catch((error) => {
        if (error.response.data.errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const createDictonary = () => (dispatch, getState) => {
  const { dictionaryList } = getState().dictionaries;
  const { username, workspaceId } = { ...getState().app?.session?.userInfo };
  const dictionary = {
    workspaceId,
    updatedBy: username,
    createdBy: username,
    type: "Quality Practices",
    name: getNextAvailableTitle("New Dictionary", dictionaryList),
    comment: `created by ${username}`,
    categories: [
      {
        name: "Default Category",
        description: "",
        terms: [],
      },
    ],
  };

  dispatch(setDictionary(formatResponse(dictionary)));
  dispatch(setModeToEdit());
  dispatch(disableNavBarAndSideBar(true));
};

export const importDictionaryFromArchive =
  (formData, navigate, fileExtension) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post(`/dictionary/import/${fileExtension}`, formData, {
        headers: {
          "Content-Type": MULTIPLE_DICTIONARIES,
        },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setDictionary(formatResponse(data)));
        dispatch(hideModal(modalTypes.IMPORT_DICTIONARY));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `"${
              formData.get("importedFile").name
            }" imported successfully`,
          })
        );
        navigate(getDictionaryPath(data.id));
      })
      .catch((error) => {
        dispatch(hideModal(modalTypes.IMPORT_FILE));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.danger,
            text: getErrorMessage(error),
          })
        );
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const restoreDictionaries = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/dictionary/restore", formData, {
      headers: {
        "Content-Type": MULTIPLE_DICTIONARIES,
      },
    })
    .then(async (response) => {
      const { importedDictionaries, dictionariesToImport, dictionaries } =
        response.data;

      dispatch(getDictionaries(dictionaries));
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showGlobalAlert({
          variant:
            importedDictionaries === dictionariesToImport
              ? alertVariants.success
              : alertVariants.warning,
          text: `Restore Dictionaries Complete. We successfully restored ${importedDictionaries}/${dictionariesToImport}`,
        })
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showInlineAlert({
          variant: alertVariants.danger,
          text: getErrorMessage(error),
        })
      );
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const backupAllDictionaries = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/dictionary/backupAll")
    .then(async (response) => {
      const { data } = response;
      const url = `/dictionary/backupAll?uniqueFolderName=${data}`;
      downloadWithIFrame(url);
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const duplicateDictionary =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post("/dictionary/edit/clone", null, {
        params: { dictionaryId, dictionaryName },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(setDictionary(formatResponse(data)));
        dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
      })
      .catch((error) => {
        if (error.response.data.errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const setDefaultDictionary =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .post("/dictionary/edit/setDefault", null, {
        params: { dictionaryId },
      })
      .then(async (response) => {
        const { data } = response;
        dispatch(hideModal(modalTypes.CONFIRMATION));
        dispatch(getDictionaries(data));
        dispatch(
          showGlobalAlert({
            variant: alertVariants.success,
            text: `${dictionaryName} successfully set as the default dictionary`,
          })
        );
      })
      .catch((error) => {
        if (error.response.data.errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const exportDictionaryXml =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .get("/dictionary/export/xml", {
        responseType: "blob",
        params: { dictionaryId },
      })
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(data, `${dictionaryName}.xml`);
      })
      .catch(async (error) => {
        const data = await error.response.data.text();
        const { errorCode } = JSON.parse(data);
        if (errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const exportDictionaryCsv =
  (dictionaryId, dictionaryName) => async (dispatch) => {
    dispatch(addToJobQueue("genericJob"));

    await localhost
      .get("/dictionary/export/csv", {
        responseType: "blob",
        params: { dictionaryId },
      })
      .then(async (response) => {
        const { data } = response;
        downloadFileFromResponse(data, `${dictionaryName}.csv`);
      })
      .catch(async (error) => {
        const data = await error.response.data.text();
        const { errorCode } = JSON.parse(data);
        if (errorCode === "DICTIONARY_LOCKED") {
          handleMenuOptionLockError(dispatch);
        } else {
          dispatch(showErrorModal(error));
        }
      })
      .finally(() => {
        dispatch(clearJobQueue());
      });
  };

export const importDictionaryCsv = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/dictionary/update/csv", formData, {
      headers: {
        "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setDictionary(formatResponse(data)));
      dispatch(setModeToView());
      dispatch(disableNavBarAndSideBar(false));
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `"${formData.get("importedFile").name}" imported successfully`,
        })
      );
    })
    .catch((error) => {
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.danger,
          text: getErrorMessage(error),
        })
      );
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};
